import { arraysEqual } from "./locationStateStackLogic";

export const MAX_FISHIPEDIA_HISTORY_POP_DEPTH = 3;

const HISTORY_TRANSACTION_VERSION = 1;
const historyDocumentToken = createHistoryToken();
const historyLedger = {
  entries: [],
  index: -1,
};
const historyTransactions = new Map();
let pendingTransition = null;

function createHistoryToken() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : null;
  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeStack(value) {
  return Array.isArray(value)
    ? value.filter((id) => typeof id === "string" && id.length > 0)
    : [];
}

function buildLedgerEntry(location) {
  if (!location || typeof location.key !== "string" || !location.key) {
    return null;
  }
  return {
    key: location.key,
    pathname: typeof location.pathname === "string" ? location.pathname : "/",
    search: typeof location.search === "string" ? location.search : "",
    hash: typeof location.hash === "string" ? location.hash : "",
    appOverlayStack: normalizeStack(location.state?.appOverlayStack),
    mapOverlayStack: normalizeStack(location.state?.mapOverlayStack),
  };
}

function routeMatches(entry, route) {
  return (
    entry?.pathname === route?.pathname &&
    entry?.search === (route?.search || "") &&
    entry?.hash === (route?.hash || "")
  );
}

function signatureMatchesLocation(signature, location) {
  const current = buildLedgerEntry(location);
  if (!signature || !current) return false;
  return (
    signature.key === current.key &&
    routeMatches(current, signature) &&
    arraysEqual(
      current[signature.stackKey] || [],
      signature.overlayIds || []
    )
  );
}

function toStackSignature(entry, stackKey) {
  return {
    key: entry.key,
    pathname: entry.pathname,
    search: entry.search,
    hash: entry.hash,
    stackKey,
    overlayIds: [...(entry[stackKey] || [])],
  };
}

function isSupportedOverlayStack(stack) {
  return (
    Array.isArray(stack) &&
    stack.length <= 10 &&
    stack.every((id) => typeof id === "string" && id.length > 0) &&
    new Set(stack).size === stack.length
  );
}

function getSupportedIntermediateStacks({
  stackKey,
  parentStack,
  childId,
  currentStack,
}) {
  if (
    stackKey === "mapOverlayStack" &&
    childId === "weather-fish" &&
    arraysEqual(parentStack, ["weather-overlay"]) &&
    arraysEqual(currentStack, ["weather-overlay", "weather-fish"])
  ) {
    return [];
  }

  if (
    stackKey === "mapOverlayStack" &&
    childId === "spot-post-info" &&
    arraysEqual(parentStack, ["spot-legend"])
  ) {
    if (arraysEqual(currentStack, ["spot-legend", "spot-post-info"])) {
      return [];
    }
    if (
      arraysEqual(currentStack, [
        "spot-legend",
        "spot-reviews",
        "spot-post-info",
      ])
    ) {
      return [["spot-legend", "spot-reviews"]];
    }
  }

  if (
    stackKey === "appOverlayStack" &&
    childId === "info-popup" &&
    arraysEqual(parentStack, [])
  ) {
    if (arraysEqual(currentStack, ["info-popup"])) return [];
    if (arraysEqual(currentStack, ["info-popup", "profile"])) {
      return [["profile"]];
    }
    if (arraysEqual(currentStack, ["info-popup", "user-profile"])) {
      return [["user-profile"]];
    }
  }

  return null;
}

function statesEqual(left, right) {
  if (left === right) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => statesEqual(value, right[index]))
    );
  }
  if (
    !left ||
    !right ||
    typeof left !== "object" ||
    typeof right !== "object"
  ) {
    return false;
  }
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  return (
    arraysEqual(leftKeys, rightKeys) &&
    leftKeys.every((key) => statesEqual(left[key], right[key]))
  );
}

function finalLocationMatches(location, destination, state) {
  const entry = buildLedgerEntry(location);
  return (
    routeMatches(entry, destination) &&
    statesEqual(location?.state ?? null, state ?? null)
  );
}

function getExpectedRestoreAction(context) {
  if (context?.type === "map-species") return "restore-map-species";
  if (context?.type !== "post-info") return "";
  return context.surface === "map"
    ? "restore-map-post-info"
    : "restore-feed-post-info";
}

function canonicalFeedFallback(navigate) {
  if (typeof navigate !== "function") return;
  navigate("/", { replace: true, state: null });
}

function consumeHistoryTransaction(attemptId) {
  const transaction = historyTransactions.get(attemptId);
  if (transaction) transaction.phase = "consumed";
  if (pendingTransition?.attemptId === attemptId) {
    pendingTransition = null;
  }
}

function failHistoryTransition(navigate, attemptId) {
  consumeHistoryTransaction(attemptId);
  canonicalFeedFallback(navigate);
  return "fallback";
}

export function recordFishipediaRouterLocation(location, navigationType) {
  const entry = buildLedgerEntry(location);
  if (!entry) return false;

  const current = historyLedger.entries[historyLedger.index];
  if (current?.key === entry.key) {
    historyLedger.entries[historyLedger.index] = entry;
    return true;
  }

  if (navigationType === "PUSH") {
    historyLedger.entries.splice(
      historyLedger.index + 1,
      historyLedger.entries.length,
      entry
    );
    historyLedger.index += 1;
    return true;
  }

  if (navigationType === "REPLACE" && historyLedger.index >= 0) {
    historyLedger.entries[historyLedger.index] = entry;
    return true;
  }

  if (navigationType === "POP") {
    const knownIndex = historyLedger.entries.findIndex(
      (candidate) => candidate.key === entry.key
    );
    if (knownIndex >= 0) {
      historyLedger.index = knownIndex;
      historyLedger.entries[knownIndex] = entry;
      return true;
    }
  }

  historyLedger.entries = [entry];
  historyLedger.index = 0;
  return false;
}

export function createFishipediaHistoryTransaction({
  location,
  restoreAttemptId,
  returnTo,
  stackKey,
  parentStack,
  childId,
}) {
  if (pendingTransition) return null;

  const normalizedAttemptId = String(restoreAttemptId || "").trim();
  const currentEntry = buildLedgerEntry(location);
  const ledgerCurrent = historyLedger.entries[historyLedger.index];
  if (
    !normalizedAttemptId ||
    !currentEntry ||
    !ledgerCurrent ||
    ledgerCurrent.key !== currentEntry.key ||
    !routeMatches(currentEntry, returnTo) ||
    !["appOverlayStack", "mapOverlayStack"].includes(stackKey) ||
    !isSupportedOverlayStack(parentStack)
  ) {
    return null;
  }

  const currentStack = currentEntry[stackKey] || [];
  const expectedIntermediateStacks = getSupportedIntermediateStacks({
    stackKey,
    parentStack,
    childId,
    currentStack,
  });
  if (!expectedIntermediateStacks) {
    return null;
  }

  const targetIndex =
    historyLedger.index - expectedIntermediateStacks.length - 1;
  const successPopDepth = historyLedger.index - targetIndex;
  const terminalPopDepth = successPopDepth;
  if (
    targetIndex < 0 ||
    successPopDepth < 1 ||
    successPopDepth > MAX_FISHIPEDIA_HISTORY_POP_DEPTH ||
    terminalPopDepth > MAX_FISHIPEDIA_HISTORY_POP_DEPTH
  ) {
    return null;
  }

  const targetEntry = historyLedger.entries[targetIndex];
  if (
    !routeMatches(targetEntry, returnTo) ||
    !arraysEqual(targetEntry[stackKey] || [], parentStack)
  ) {
    return null;
  }
  const chain = historyLedger.entries.slice(targetIndex, historyLedger.index + 1);
  const keys = chain.map((entry) => entry.key);
  if (new Set(keys).size !== keys.length) return null;

  const intermediateEntries = historyLedger.entries.slice(
    targetIndex + 1,
    historyLedger.index
  );
  if (
    intermediateEntries.length !== expectedIntermediateStacks.length ||
    intermediateEntries.some(
      (entry, index) =>
        !routeMatches(entry, returnTo) ||
        !arraysEqual(
          entry[stackKey] || [],
          expectedIntermediateStacks[index]
        )
    )
  ) {
    return null;
  }

  const transaction = {
    version: HISTORY_TRANSACTION_VERSION,
    documentToken: historyDocumentToken,
    transactionId: normalizedAttemptId,
    transactionToken: createHistoryToken(),
    stackKey,
    childId,
    launchIndex: historyLedger.index,
    launch: toStackSignature(currentEntry, stackKey),
    target: toStackSignature(historyLedger.entries[targetIndex], stackKey),
    // The exact parent/base is the transaction-owned cleanup anchor. It may be
    // the first router entry and may legitimately carry React Router's
    // reserved-looking `default` key.
    anchor: toStackSignature(historyLedger.entries[targetIndex], stackKey),
    intermediates: intermediateEntries.map((entry) =>
      toStackSignature(entry, stackKey)
    ),
    successPopDepth,
    terminalPopDepth,
  };

  historyTransactions.set(normalizedAttemptId, {
    transaction,
    phase: "registered",
    fishipediaEntryKey: "",
    restoreEntryKey: "",
    closeDispatched: false,
  });
  return transaction;
}

export function bindFishipediaHistoryEntry(context, location) {
  const attemptId = String(context?.restoreAttemptId || "").trim();
  const registered = historyTransactions.get(attemptId);
  const current = buildLedgerEntry(location);
  if (
    !registered ||
    registered.phase !== "registered" ||
    registered.transaction.transactionToken !==
      context?.historyTransaction?.transactionToken ||
    !current ||
    current.pathname !== "/fishipedia" ||
    historyLedger.index !== registered.transaction.launchIndex
  ) {
    return false;
  }
  if (registered.fishipediaEntryKey) {
    return registered.fishipediaEntryKey === current.key;
  }
  registered.fishipediaEntryKey = current.key;
  return true;
}

export function dispatchFishipediaCloseAction({
  navigate,
  location,
  closeAction,
}) {
  if (typeof navigate !== "function") return "ignored";
  const context = location?.state?.fishipediaReturnContext;
  const attemptId = String(context?.restoreAttemptId || "").trim();
  if (closeAction?.action === "fallback-feed") {
    consumeHistoryTransaction(attemptId);
    canonicalFeedFallback(navigate);
    return "fallback";
  }

  const registered = historyTransactions.get(attemptId);
  if (registered?.closeDispatched) return "ignored";
  const transaction = registered?.transaction;
  const current = buildLedgerEntry(location);
  const nextState = closeAction?.options?.state;
  const nextContext = nextState?.fishipediaRestoreContext;
  const marker = nextState?.fishipediaRestoredStack;
  const expectedAction = getExpectedRestoreAction(context);
  const expectedChildStack = transaction
    ? [...transaction.target.overlayIds, transaction.childId]
    : [];
  if (
    !expectedAction ||
    closeAction?.action !== expectedAction ||
    closeAction?.options?.replace !== true ||
    !registered ||
    registered.phase !== "registered" ||
    transaction.transactionToken !== context?.historyTransaction?.transactionToken ||
    registered.fishipediaEntryKey !== current?.key ||
    current?.pathname !== "/fishipedia" ||
    historyLedger.index !== transaction.launchIndex ||
    !routeMatches(closeAction?.to, context.returnTo) ||
    nextContext?.restoreAttemptId !== attemptId ||
    nextContext?.historyTransaction?.transactionToken !==
      transaction.transactionToken ||
    marker?.attemptId !== attemptId ||
    marker?.stackKey !== transaction.stackKey ||
    marker?.action !== expectedAction ||
    marker?.sourceEntryKey !== current.key ||
    !arraysEqual(nextState?.[transaction.stackKey] || [], expectedChildStack)
  ) {
    return failHistoryTransition(navigate, attemptId);
  }

  registered.closeDispatched = true;
  navigate(closeAction.to, closeAction.options);
  return "dispatched";
}

function bindRestoreHistoryEntry(location) {
  const context = location?.state?.fishipediaRestoreContext;
  const marker = location?.state?.fishipediaRestoredStack;
  const attemptId = String(context?.restoreAttemptId || "").trim();
  const registered = historyTransactions.get(attemptId);
  const transaction = registered?.transaction;
  const current = buildLedgerEntry(location);
  const expectedChildStack = transaction
    ? [...transaction.target.overlayIds, transaction.childId]
    : [];
  if (
    !registered ||
    registered.phase !== "registered" ||
    !registered.closeDispatched ||
    transaction.transactionToken !== context?.historyTransaction?.transactionToken ||
    !registered.fishipediaEntryKey ||
    marker?.sourceEntryKey !== registered.fishipediaEntryKey ||
    marker?.attemptId !== attemptId ||
    marker?.stackKey !== transaction.stackKey ||
    marker?.action !== getExpectedRestoreAction(context) ||
    !current ||
    historyLedger.index !== transaction.launchIndex ||
    !routeMatches(current, context.returnTo) ||
    !arraysEqual(current[transaction.stackKey] || [], expectedChildStack)
  ) {
    return false;
  }

  if (registered.restoreEntryKey) {
    return registered.restoreEntryKey === current.key;
  }
  registered.restoreEntryKey = current.key;
  return true;
}

function validateHistorySubmission(location, states) {
  const transaction = states?.context?.historyTransaction;
  const attemptId = states?.context?.restoreAttemptId;
  const registered = historyTransactions.get(attemptId);
  const marker = location?.state?.fishipediaRestoredStack;
  const current = buildLedgerEntry(location);
  if (
    !transaction ||
    transaction.version !== HISTORY_TRANSACTION_VERSION ||
    transaction.documentToken !== historyDocumentToken ||
    transaction.transactionId !== attemptId ||
    !registered ||
    registered.transaction.transactionToken !== transaction.transactionToken ||
    registered.phase !== "registered" ||
    !registered.fishipediaEntryKey ||
    !registered.restoreEntryKey ||
    registered.restoreEntryKey !== current?.key ||
    marker?.sourceEntryKey !== registered.fishipediaEntryKey ||
    marker?.attemptId !== attemptId ||
    marker?.stackKey !== transaction.stackKey ||
    marker?.action !== getExpectedRestoreAction(states.context) ||
    historyLedger.index !== transaction.launchIndex ||
    current?.key !== location.key ||
    !routeMatches(current, states.context.returnTo) ||
    !arraysEqual(current[transaction.stackKey], states.childStack)
  ) {
    return null;
  }

  const expectedBehind = [
    transaction.target,
    ...transaction.intermediates,
  ];
  const ledgerBehind = historyLedger.entries.slice(
    transaction.launchIndex - expectedBehind.length,
    transaction.launchIndex
  );
  if (
    ledgerBehind.length !== expectedBehind.length ||
    expectedBehind.some((signature, index) =>
      !signatureMatchesLocation(signature, {
        ...ledgerBehind[index],
        state: {
          [signature.stackKey]: ledgerBehind[index][signature.stackKey],
        },
      })
    )
  ) {
    return null;
  }
  return registered;
}

function buildPopLandings(transaction) {
  return [
    ...transaction.intermediates.slice().reverse(),
    transaction.target,
  ];
}

export function submitFishipediaRestoreHistory({
  navigate,
  location,
  states,
  outcome,
  terminalDestination = null,
  terminalState = undefined,
}) {
  if (typeof navigate !== "function") return "ignored";

  const attemptId = states?.context?.restoreAttemptId;
  const existing = historyTransactions.get(attemptId);
  if (existing && existing.phase !== "registered") {
    return "ignored";
  }
  if (pendingTransition) return "ignored";
  if (
    !isValidRestoreHistoryStates(states) ||
    !["success", "terminal"].includes(outcome)
  ) {
    return failHistoryTransition(navigate, attemptId);
  }

  const registered = validateHistorySubmission(location, states);
  if (!registered) {
    return failHistoryTransition(navigate, attemptId);
  }

  const transaction = registered.transaction;
  const needsPop =
    outcome === "terminal" || transaction.successPopDepth > 1;
  registered.phase = needsPop ? "awaiting-pop" : "consumed";

  if (!needsPop) {
    const committed = commitFishipediaRestoreHistory(navigate, location, states);
    consumeHistoryTransaction(attemptId);
    return committed ? "complete" : "fallback";
  }

  const landings = buildPopLandings(transaction);
  const expectedDepth =
    outcome === "terminal"
      ? transaction.terminalPopDepth
      : transaction.successPopDepth;
  if (
    landings.length !== expectedDepth ||
    expectedDepth < 1 ||
    expectedDepth > MAX_FISHIPEDIA_HISTORY_POP_DEPTH ||
    pendingTransition
  ) {
    return failHistoryTransition(navigate, attemptId);
  }

  pendingTransition = {
    attemptId,
    outcome,
    states,
    landings,
    terminalDestination,
    terminalState,
    phase: "awaiting-pop",
    stageIndex: null,
    lastProcessedKey: "",
  };
  navigate(-1);
  return "pending";
}

function finalizePendingTransition(navigate, location, pending) {
  const { outcome, states } = pending;

  if (outcome === "success") {
    const destination = states.context.returnTo;
    pending.phase = "awaiting-parent-replace";
    pending.stageIndex = historyLedger.index;
    pending.phaseSourceKey = location.key;
    navigate(destination, { replace: true, state: states.parentState });
    return "pending";
  }

  const destination = pending.terminalDestination || states.context.returnTo;
  const state =
    pending.terminalState !== undefined
      ? pending.terminalState
      : states.terminalParentState ?? states.parentState;
  pending.phase = "awaiting-terminal-replace";
  pending.stageIndex = historyLedger.index;
  pending.phaseSourceKey = location.key;
  navigate(destination, { replace: true, state });
  return "pending";
}

function continueFinalNavigation(navigate, location, navigationType, pending) {
  const { attemptId, states } = pending;
  if (pending.phaseSourceKey === location.key) return "pending";

  if (pending.phase === "awaiting-parent-replace") {
    const destination = states.context.returnTo;
    if (
      navigationType !== "REPLACE" ||
      historyLedger.index !== pending.stageIndex ||
      !finalLocationMatches(location, destination, states.parentState)
    ) {
      return failHistoryTransition(navigate, attemptId);
    }

    pending.phase = "awaiting-child-push";
    pending.stageIndex = historyLedger.index + 1;
    pending.phaseSourceKey = location.key;
    navigate(destination, { state: states.childState });
    return "pending";
  }

  if (pending.phase === "awaiting-child-push") {
    if (
      navigationType !== "PUSH" ||
      historyLedger.index !== pending.stageIndex ||
      !finalLocationMatches(
        location,
        states.context.returnTo,
        states.childState
      )
    ) {
      return failHistoryTransition(navigate, attemptId);
    }

    consumeHistoryTransaction(attemptId);
    return "complete";
  }

  if (pending.phase === "awaiting-terminal-replace") {
    const destination = pending.terminalDestination || states.context.returnTo;
    const state =
      pending.terminalState !== undefined
        ? pending.terminalState
        : states.terminalParentState ?? states.parentState;
    if (
      navigationType !== "REPLACE" ||
      historyLedger.index !== pending.stageIndex ||
      !finalLocationMatches(location, destination, state)
    ) {
      return failHistoryTransition(navigate, attemptId);
    }

    consumeHistoryTransaction(attemptId);
    return "complete";
  }

  return failHistoryTransition(navigate, attemptId);
}

export function continueFishipediaRestoreHistory(
  navigate,
  location,
  navigationType
) {
  recordFishipediaRouterLocation(location, navigationType);
  bindRestoreHistoryEntry(location);
  const pending = pendingTransition;
  if (!pending) return "idle";
  if (pending.phase !== "awaiting-pop") {
    return continueFinalNavigation(navigate, location, navigationType, pending);
  }
  if (navigationType !== "POP") {
    return failHistoryTransition(navigate, pending.attemptId);
  }

  if (pending.lastProcessedKey === location.key) return "pending";

  const expected = pending.landings[0];
  if (!signatureMatchesLocation(expected, location)) {
    return failHistoryTransition(navigate, pending.attemptId);
  }
  pending.lastProcessedKey = location.key;
  pending.landings.shift();

  if (pending.landings.length > 0) {
    navigate(-1);
    return "pending";
  }

  return finalizePendingTransition(navigate, location, pending);
}

export function isFishipediaHistoryTransitionPending(stackKey = "") {
  return Boolean(
    pendingTransition &&
      (!stackKey || pendingTransition.states.stackKey === stackKey)
  );
}

export function resetFishipediaHistoryCoordinator() {
  historyLedger.entries = [];
  historyLedger.index = -1;
  historyTransactions.clear();
  pendingTransition = null;
}

function isUniqueStringStack(stack) {
  return (
    Array.isArray(stack) &&
    stack.every((id) => typeof id === "string" && id.length > 0) &&
    new Set(stack).size === stack.length
  );
}

function stateContainsStack(state, stateKey, expectedStack) {
  const actualStack = Array.isArray(state?.[stateKey])
    ? state[stateKey]
    : [];
  return arraysEqual(actualStack, expectedStack);
}

function isValidRestoreHistoryStates(states) {
  if (!states || typeof states !== "object") return false;

  const {
    stackKey,
    parentStack,
    childStack,
    parentState,
    terminalParentState,
    childState,
  } = states;
  if (typeof stackKey !== "string" || !stackKey) return false;
  if (!isUniqueStringStack(parentStack) || !isUniqueStringStack(childStack)) {
    return false;
  }
  if (childStack.length !== parentStack.length + 1) return false;
  if (!arraysEqual(childStack.slice(0, parentStack.length), parentStack)) {
    return false;
  }
  if (!stateContainsStack(parentState, stackKey, parentStack)) return false;
  if (
    terminalParentState !== undefined &&
    !stateContainsStack(terminalParentState, stackKey, parentStack)
  ) {
    return false;
  }
  if (!stateContainsStack(childState, stackKey, childStack)) return false;

  return true;
}

/**
 * Finalize an approved Fishipedia restoration in the retired child slot.
 *
 * Fishipedia replaced the original child, leaving the genuine parent directly
 * behind it. The X action replaces Fishipedia with a pending child state, and
 * this replacement consumes the one-time restore state without adding another
 * browser entry. Callers own readiness checks and the restore-attempt claim.
 */
export function commitFishipediaRestoreHistory(navigate, location, states) {
  if (
    typeof navigate !== "function" ||
    !location ||
    typeof location.pathname !== "string" ||
    !isValidRestoreHistoryStates(states)
  ) {
    return false;
  }

  const destination = {
    pathname: location.pathname,
    search: typeof location.search === "string" ? location.search : "",
    hash: typeof location.hash === "string" ? location.hash : "",
  };

  navigate(destination, {
    replace: true,
    state: states.childState,
  });
  return true;
}
